import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { getLoginPath } from "@/const";
import { trpc } from "@/lib/trpc";
import { hasFullSiteAccess } from "@shared/siteAccess";

/**
 * Keeps paid-member pages hidden until the server-backed session and
 * entitlement are both known. Client state can never grant access.
 */
export default function RequirePaidMember({ children }: { children: React.ReactNode }) {
  const [, navigate] = useLocation();
  const session = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const entitlement = trpc.members.myEntitlement.useQuery(undefined, {
    enabled: Boolean(session.data),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const sessionSettled = !session.isLoading;
  const entitlementSettled = !session.data || !entitlement.isLoading;
  const settled = sessionSettled && entitlementSettled;
  const hasAccess = hasFullSiteAccess({
    role: session.data?.role,
    accessState: entitlement.data?.accessState,
  });

  useEffect(() => {
    if (!settled || hasAccess) return;

    const requested = `${window.location.pathname}${window.location.search}`;
    navigate(session.data ? "/membership" : getLoginPath(requested), { replace: true });
  }, [hasAccess, navigate, session.data, settled]);

  if (!settled || !hasAccess) {
    return (
      <div
        role="status"
        aria-label={!settled ? "Checking membership access" : "Redirecting to membership"}
        style={{ minHeight: "100vh", background: "#080C18", display: "grid", placeItems: "center", color: "#94A3B8" }}
      >
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}